import React, { useMemo, useRef } from "react";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import paths from "@icarius-router/paths";
import CommonPage from "@icarius-common/commonPage";
import { getLocalizedString } from "@icarius-localization/strings";
import ProgressRenderer from "@icarius-table/renderersAndFilters/progressRenderer";
import ProgressFilter from "@icarius-table/renderersAndFilters/progressFilter";
import ProgressByRankingRendererDynamicColor from "@icarius-table/renderersAndFilters/progressByRankingRendererDynamicColor";
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import FeedbackFormDialog from "@icarius-pages/feed/components/form/formDialog";
import { createFeedbackPublicationAction } from "@icarius-pages/feedback/actions";
import usePerformanceEvaluations from "./usePerformanceEvaluations";
import InitialScreen from "./initialScreen";
import SummaryScreen from "./summaryScreen";
import AcceptRejectDialog from "./acceptRejectDialog";
import GraphicDialog from "./graphicDialog";
import FeedbackPublication from "./feedbackPublication";
import { getColumnDefByPage } from "@icarius-table/columnDefs";

const PerformanceEvaluations = () => {

  const {
    level,
    data,
    summaryData,
    isAcceptRejectEnabled,
    isSocietySignActive,
    isLoading,
    state,
    handlers,

    //feedback
    typeList,
    modeList,
    people,
    groups,
    tasks,
    goals,
    comments,
    isLoadingFeedback,
  } = usePerformanceEvaluations();

  const ownGridRef = useRef(); // tengo que usar una ref propia para el aggrid porque tengo 3 commonPage y al salir y volver se pierden las ref internas

  const goBackButton = () => (
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handlers.handleUnselectProcess}
      type={"goBack"}
    />
  )

  const acceptRejectButton = () => isAcceptRejectEnabled && level === 'C' && ['K'].includes(state.selectedProcess?.processStatus) && (
    <CustomIconButton
      title={getLocalizedString("acceptOrObjectEvaluation")}
      onClick={() => handlers.handleOpenAcceptReject(ownGridRef.current)}
    >
      <ThumbsUpDownIcon />
    </CustomIconButton>
  )

  const viewSummaryButton = () => (
    <CustomIconButton
      title={getLocalizedString("finalSummary")}
      onClick={() => handlers.handleOpenSummary(ownGridRef.current)}
      type={"assessment"}
    />
  )

  const downloadItem = () => (
    <CustomIconButton
      title={getLocalizedString("downloadFinalEvaluationSummary")}
      onClick={() => handlers.handleDownloadSummary(ownGridRef.current)}
      type={"download"}
    />
  )

  const viewGraphicDialogButton = () => (
    <CustomIconButton
      title={getLocalizedString("viewGaussBell")}
      onClick={() => handlers.handleOpenGraphicDialog(ownGridRef.current)}
    >
      <ShowChartIcon />
    </CustomIconButton>
  )

  const isLoadingEverything = isLoading || isLoadingFeedback.gridData || isLoadingFeedback.privacyData || isLoadingFeedback.feed;

  const ownColumnDef = useMemo(() => {
    let columnDefinitions = getColumnDefByPage(paths.performanceSummaryAndResults);
    if (state.selectedProcess?.isPointsAssignationEnabled) return columnDefinitions;
    return columnDefinitions.filter((item) => "pointsAssigned" !== item.field)
  }, [state.selectedProcess])

  return (
    <>
      {
        !state.selectedProcess &&
        <InitialScreen
          isLoading={isLoadingEverything}
          data={data}
          handleSelectProcess={handlers.handleSelectProcess}
        />
      }
      {
        state.selectedEvaluation &&
        <SummaryScreen
          isLoading={isLoadingEverything}
          level={level}
          data={state.selectedEvaluation}
          summaryData={summaryData}
          process={state.selectedProcess}
          evaluatedName={state.selectedEvaluation?.evaluatedName}
          handleOpenViewFeedback={handlers.handleOpenViewFeedback}
          handleClose={handlers.handleCloseSummary}
          subtitle={state.selectedEvaluation.status}
        />
      }
      {
        state.selectedProcess && !state.selectedEvaluation &&
        <CommonPage
          isLoading={isLoadingEverything}
          title={`${getLocalizedString("performanceSummaryAndResults")}: ${state.selectedProcess.processName} - ${getLocalizedString("delegationDateFrom")} ${state.selectedProcess.fromDate} - ${getLocalizedString("delegationDateTo")} ${state.selectedProcess.endDate}`}
          gridTitle={`${getLocalizedString("performanceSummaryAndResults")} - ${state.selectedProcess.processName}`}
          ownColumnDef={ownColumnDef}
          rowData={state.selectedProcess?.data}
          menuItems={[goBackButton, viewSummaryButton, viewGraphicDialogButton, acceptRejectButton, downloadItem]}
          ownGridRef={ownGridRef}
          handleRowClick={handlers.handleRowClick}
          hasExpand
          hasHelp
          frameworkComponents={{ ProgressByRankingRendererDynamicColor, ProgressRenderer, ProgressFilter }}
        >
        </CommonPage>
      }
      {
        state.acceptRejectData &&
        <AcceptRejectDialog
          open={Boolean(state.acceptRejectData)}
          data={state.acceptRejectData}
          processData={state.selectedProcess}
          isSocietySignActive={isSocietySignActive}
          handleClose={handlers.handleCloseAcceptReject}
        />
      }
      {
        state.feedbackFormData &&
        <FeedbackFormDialog
          open={Boolean(state.feedbackFormData)}
          preselectedData={{
            userCodes: [state.feedbackFormData?.evaluatedCode],
            type: 'ENT_DES',
            mode: 'P',
            reference: state.selectedProcess.processName,
            evaluationProcess: state.selectedProcess.process,
          }}
          data={null}
          goals={goals}
          modeList={modeList}
          typeList={typeList}
          groups={groups}
          tasks={tasks}
          people={people}
          statusToCreate={'E'}
          createPublicationAction={(dataToSend) => createFeedbackPublicationAction(dataToSend, true)}
          isLoading={isLoadingEverything}
          handleClose={handlers.handleCloseFeedbackForm}
          handleAfterCreateCallback={handlers.handleCreateFeedbackCallback}
          isFeedback
          hideGoals
        />
      }
      {
        state.viewFeedbackIsOpen &&
        <FeedbackPublication
          isLoading={isLoadingFeedback}
          data={summaryData.feedback}
          people={people}
          groups={groups}
          tasks={tasks}
          comments={comments}
          handleClose={handlers.handleCloseViewFeedback}
        />
      }
      {
        state.graphicDialogData && state.selectedProcess?.noteList &&
        <GraphicDialog
          open={Boolean(state.graphicDialogData)}
          data={state.graphicDialogData}
          handleClose={handlers.handleCloseGraphicDialog}
          noteList={state.selectedProcess.noteList}
        />
      }
    </>
  )
}

export default PerformanceEvaluations;